import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react'
import { useReactFlow } from '@xyflow/react'
import { Send, MessageSquare, ChevronDown, RotateCcw } from 'lucide-react'
import { useAICallbacks } from '../../../../context/aiGeneration'
import { useActiveChatContext } from '../../../../context/activeChat'
import { useScrollIntent } from '../../hooks/useScrollIntent'
import { ChatMarkdown } from './ChatMarkdown'
import { ToolCallCard } from './ToolCallCard'
import { SuggestionPicker } from './SuggestionPicker'
import { useNodeChat } from './useNodeChat'
import { useChatSuggestionSync } from './useChatSuggestionSync'
import {
  ChatPanel,
  ChatToggle,
  ChatToggleLabel,
  ChatToggleCount,
  ChatToggleUnreadBadge,
  ChatToggleTypingDots,
  ChatToggleChevron,
  ChatContainer,
  ChatHeaderActions,
  ResetButton,
  MessageAreaWrapper,
  ScrollFadeTop,
  MessageArea,
  MessageRow,
  MessageGroup,
  MessageBubble,
  TypingBubble,
  TypingDot,
  InputArea,
  ChatInput,
  SendButton,
  MessageTimestamp,
} from './NodeChatStyles'

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

interface NodeChatProps {
  nodeId: string
  isOpen: boolean
  onReady?: (api: { autoGenerate: () => void }) => void
}

const NodeChatInner = ({ nodeId, isOpen, onReady }: NodeChatProps): React.ReactElement => {
  const { getNode } = useReactFlow()
  const [inputValue, setInputValue] = useState('')
  const { openChat, closeChat } = useActiveChatContext()
  const {
    messages,
    isLoading,
    sendMessage,
    autoGenerate: autoGenerateBase,
    approveToolCall,
    rejectToolCall,
    resetChat,
    injectSuggestionMessage,
  } = useNodeChat(nodeId)

  // Wrap autoGenerate to also open the chat panel before generating
  const autoGenerate = useCallback(() => {
    openChat(nodeId)
    autoGenerateBase()
  }, [openChat, nodeId, autoGenerateBase])

  // Expose chat API to parent components (e.g. IdeaNode CTA)
  useEffect(() => {
    onReady?.({ autoGenerate })
  }, [onReady, autoGenerate])
  const aiCallbacks = useAICallbacks()
  const { externalSuggestions, isExternalGenerating, getSuggestionsForIds } = useChatSuggestionSync(
    nodeId,
    messages
  )
  const [canScrollUp, setCanScrollUp] = useState(false)

  // Track how many messages were visible when the chat was last open.
  // A dot appears when new messages arrive while minimized.
  const [seenCount, setSeenCount] = useState(messages.length)
  useEffect(() => {
    if (isOpen) setSeenCount(messages.length)
  }, [isOpen, messages.length])
  const hasUnread = !isOpen && messages.length > seenCount

  const messageAreaRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const updateCanScrollUp = useCallback(() => {
    const el = messageAreaRef.current
    if (!el) return
    setCanScrollUp(el.scrollTop > 10)
  }, [])

  const handleMessageAreaScroll = useCallback(() => {
    updateCanScrollUp()
  }, [updateCanScrollUp])

  // Count resolved suggestions for the latest message
  const lastMsg = messages[messages.length - 1]
  const lastMsgSuggestionCount = lastMsg?.suggestionIds
    ? getSuggestionsForIds(lastMsg.suggestionIds).length
    : 0

  const hasContent = messages.length > 0 || isLoading || isExternalGenerating

  // Track previous isOpen to detect when chat transitions from closed → open
  const prevIsOpenRef = useRef(false)

  // Auto-scroll to bottom BEFORE paint; instant when chat first opens, smooth for new messages
  useLayoutEffect(() => {
    const wasOpen = prevIsOpenRef.current
    prevIsOpenRef.current = isOpen
    if (!isOpen || !hasContent || !messageAreaRef.current) return
    const justOpened = !wasOpen
    messageAreaRef.current.scrollTo({
      top: messageAreaRef.current.scrollHeight,
      behavior: justOpened ? 'instant' : 'smooth',
    })
    requestAnimationFrame(updateCanScrollUp)
  }, [isOpen, messages.length, isLoading, hasContent, updateCanScrollUp, lastMsgSuggestionCount])

  // Auto-resize textarea to fit content
  const resizeInput = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [])

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim()
    if (!trimmed || isLoading) return
    sendMessage(trimmed)
    setInputValue('')
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.style.height = 'auto'
      }
    })
  }, [inputValue, isLoading, sendMessage])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      e.stopPropagation()
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  const stopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation()
  }, [])

  // Inject external suggestions as messages in the chat flow.
  // Track the last-injected set so accepting/skipping (which shrinks
  // externalSuggestions) doesn't trigger another "I found N options" message —
  // only genuinely new batches should inject.
  const lastInjectedIdsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const currentIds = externalSuggestions.map((s) => s.id)

    if (currentIds.length === 0) {
      // Batch fully consumed (or none yet). Reset so the next batch is fresh.
      lastInjectedIdsRef.current = new Set()
      return
    }

    // If every current ID was already part of the last injection, this is a
    // removal from the same batch — keep the existing message intact.
    const allKnown = currentIds.every((id) => lastInjectedIdsRef.current.has(id))
    if (allKnown) return

    const content = `I found ${currentIds.length} options for you. Pick any card below to add it to your plan, or ask me to explore different directions.`
    injectSuggestionMessage(content, currentIds)
    lastInjectedIdsRef.current = new Set(currentIds)
  }, [externalSuggestions, injectSuggestionMessage])

  /** Get current value of a field from the node data */
  const getFieldValue = useCallback(
    (field: string): string => {
      const node = getNode(nodeId)
      if (!node) return ''
      const value = node.data[field]
      return typeof value === 'string' ? value : ''
    },
    [getNode, nodeId]
  )

  const canSend = inputValue.trim().length > 0 && !isLoading
  const { containerRef: scrollIntentRef, shouldCaptureWheel } = useScrollIntent()

  const handleReset = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      resetChat()
    },
    [resetChat]
  )

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isOpen) {
        closeChat()
      } else {
        openChat(nodeId)
      }
    },
    [isOpen, openChat, closeChat, nodeId]
  )

  return (
    <ChatPanel
      ref={scrollIntentRef}
      className={`nodrag${isOpen && shouldCaptureWheel ? ' nowheel' : ''}`}
      onClick={stopPropagation}
      onKeyDown={stopPropagation}
      onMouseDown={stopPropagation}
    >
      {/* Accordion toggle — always visible */}
      <ChatToggle onClick={handleToggle}>
        <MessageSquare size={12} />
        <ChatToggleLabel>
          Chat{messages.length > 0 && <ChatToggleCount>({messages.length})</ChatToggleCount>}
        </ChatToggleLabel>
        {hasUnread && <ChatToggleUnreadBadge>new</ChatToggleUnreadBadge>}
        {!isOpen && (isLoading || isExternalGenerating) && (
          <ChatToggleTypingDots>
            <TypingDot $delay="0s" />
            <TypingDot $delay="0.2s" />
            <TypingDot $delay="0.4s" />
          </ChatToggleTypingDots>
        )}
        <ChatToggleChevron $open={isOpen}>
          <ChevronDown size={12} />
        </ChatToggleChevron>
      </ChatToggle>

      {/* Chat body — only when open */}
      {isOpen && (
        <ChatContainer>
          {messages.length > 0 && (
            <ChatHeaderActions>
              <ResetButton
                type="button"
                onClick={handleReset}
                title="Reset chat"
                aria-label="Reset chat"
              >
                <RotateCcw size={11} />
                Reset
              </ResetButton>
            </ChatHeaderActions>
          )}
          {/* Messages — shown only when there is chat content */}
          {hasContent && (
            <MessageAreaWrapper>
              <ScrollFadeTop $visible={canScrollUp} />
              <MessageArea ref={messageAreaRef} onScroll={handleMessageAreaScroll}>
                {messages.map((msg) => (
                  <React.Fragment key={msg.id}>
                    {msg.content.length > 0 && (
                      <MessageRow $isUser={msg.sender === 'user'}>
                        <MessageGroup $isUser={msg.sender === 'user'}>
                          <MessageBubble $isUser={msg.sender === 'user'}>
                            {msg.sender === 'ai' ? (
                              <ChatMarkdown content={msg.content} />
                            ) : (
                              msg.content
                            )}
                          </MessageBubble>
                          <MessageTimestamp $isUser={msg.sender === 'user'}>
                            {formatTime(msg.timestamp)}
                          </MessageTimestamp>
                        </MessageGroup>
                      </MessageRow>
                    )}
                    {msg.toolCalls?.map((tc, i) => (
                      <MessageRow key={`${msg.id}-tool-${i}`} $isUser={false}>
                        <ToolCallCard
                          toolCall={tc}
                          currentValue={getFieldValue(tc.args.field)}
                          onApprove={() => approveToolCall(msg.id, i)}
                          onReject={() => rejectToolCall(msg.id, i)}
                        />
                      </MessageRow>
                    ))}
                    {msg.suggestionIds && msg.suggestionIds.length > 0 && (
                      <MessageRow key={`${msg.id}-suggestions`} $isUser={false}>
                        <SuggestionPicker
                          nodeId={nodeId}
                          suggestions={getSuggestionsForIds(msg.suggestionIds)}
                          isGenerating={false}
                          onAcceptSuggestion={(id: string) => {
                            void aiCallbacks?.toggleSuggestion(id)
                          }}
                        />
                      </MessageRow>
                    )}
                  </React.Fragment>
                ))}
                {isExternalGenerating && (
                  <MessageRow $isUser={false}>
                    <SuggestionPicker
                      nodeId={nodeId}
                      suggestions={[]}
                      isGenerating
                      onAcceptSuggestion={() => {}}
                    />
                  </MessageRow>
                )}
                {isLoading && (
                  <MessageRow $isUser={false}>
                    <TypingBubble>
                      <TypingDot $delay="0s" />
                      <TypingDot $delay="0.2s" />
                      <TypingDot $delay="0.4s" />
                    </TypingBubble>
                  </MessageRow>
                )}
              </MessageArea>
            </MessageAreaWrapper>
          )}

          {/* Input */}
          <InputArea>
            <ChatInput
              ref={inputRef}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value)
                resizeInput()
              }}
              onKeyDown={handleKeyDown}
              onMouseDown={stopPropagation}
              placeholder="Ask anything about this node..."
              rows={1}
            />
            <SendButton $disabled={!canSend} onClick={canSend ? handleSend : undefined}>
              <Send size={13} />
            </SendButton>
          </InputArea>
        </ChatContainer>
      )}
    </ChatPanel>
  )
}

export const NodeChat = React.memo(NodeChatInner)
